if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');

        /* liftsets (20 Sep, owner report: "after changing the variation it won't let me click on the second
           image"). Stock Dawn bound one click listener per thumbnail button here in the constructor, and
           cached this.elements once. A variant change morphs new children into this same element, so the
           constructor never runs again: the fresh thumbnail buttons carried no listener and the cached
           element references pointed at detached nodes. Reproduced in Chrome before the fix, clicking the
           second thumbnail after a variant change did nothing at all.
           Two changes make it survive any DOM replacement:
           1. one delegated click listener on this element, which new children inherit automatically;
           2. `elements` is now a live getter, so nothing can hold a stale node.
           The slideChanged event does not bubble, so the viewer is rebound whenever it is replaced. */
        this.addEventListener('click', this.onThumbnailClick.bind(this));
        this.bindViewer();
        this.childObserver = new MutationObserver(() => {
          this.bindViewer();
          if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
            this.removeListSemantic();
          }
        });
        this.childObserver.observe(this, { childList: true, subtree: true });

        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      connectedCallback() {
        /* liftsets (20 Sep, Codex review): re-observe and rebind if this element is moved or reattached,
           and clean up on disconnect, so no observer or viewer listener leaks across a DOM replacement. */
        if (this.childObserver) this.childObserver.observe(this, { childList: true, subtree: true });
        this.bindViewer();
      }

      disconnectedCallback() {
        if (this.childObserver) this.childObserver.disconnect();
        this.boundViewer = null;
      }

      get elements() {
        return {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
      }

      bindViewer() {
        const viewer = this.elements.viewer;
        if (!viewer || this.boundViewer === viewer) return;
        this.boundViewer = viewer;
        viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      }

      onThumbnailClick(event) {
        const item = event.target.closest('[data-target]');
        if (!item || !this.contains(item)) return;
        const thumbnails = this.elements.thumbnails;
        if (!thumbnails || !thumbnails.contains(item)) return;
        event.preventDefault();
        this.setActiveMedia(item.dataset.target, false);
      }

      onSlideChanged(event) {
        /* liftsets (20 Sep): thumbnails can be absent, for example a variant whose media sits beyond the
           thumbnail cap, and after a variant change this fires against freshly morphed children. Guard
           rather than throw, because an exception here aborts Dawn's product info update. */
        const thumbnails = this.elements.thumbnails;
        if (!thumbnails || !event.detail || !event.detail.currentElement) return;
        const thumbnail = thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        if (thumbnail) this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend && activeMedia && activeMedia.parentElement) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            /* liftsets (HX-34): a variant whose media is beyond the thumbnail cap has no thumbnail; never throw here,
               otherwise Dawn's handleUpdateProductInfo aborts before the price swap and the submit re-enable. */
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        if (!activeMedia) return;
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail && activeMedia) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
